/*
 * Service-worker policy shared by the worker (`src/service-worker.js`) and the
 * page (`src/serviceWorkerRegistration.js`). Pure functions only: no Workbox,
 * no browser globals, so the exact production rules are unit-tested as is.
 * Canon: Workout Log V1 blueprint §9.7 (app shell) and §22.3 (kill mode).
 */

// Enabled only when the build-time flag is exactly 'true' (§21.4). Any other
// value, including unset, 'TRUE', '1' or ' true', builds the kill-mode worker
// and registers nothing. No truthy parsing.
export function isServiceWorkerEnabled(value) {
  return value === "true";
}

// Workbox cache-name prefix of the Lyft Zone worker. Both modes configure it,
// so kill mode computes exactly the precache name the normal worker created.
export const LYFT_ZONE_CACHE_PREFIX = "lyft-zone";

// The permanent worker URL (relative to PUBLIC_URL). Never removed (§22.3).
export const SERVICE_WORKER_PATH = "/service-worker.js";

// A last path segment with a file extension: `/service-worker.js`,
// `/manifest.json`, `/static/js/main.1a2b3c4d.js`, `/foo.js`. Such a navigation
// never receives the SPA shell; it goes to the network (or, for a precached
// build file, to its own precached copy).
const FILE_LIKE_PATH = /\/[^/?]+\.[^/]+$/;

export function isFileLikePath(pathname) {
  return FILE_LIKE_PATH.test(pathname);
}

export function isApiPath(pathname) {
  return pathname === "/api" || pathname.startsWith("/api/");
}

/**
 * The navigation fallback matcher (§9.7): the precached `/index.html` answers a
 * request only when it is a same-origin GET navigation to an application route.
 * API traffic, non-GET requests, cross-origin requests (the API origin, OAuth
 * providers, Google Fonts, R2 media) and file-like paths never match.
 */
export function isAppShellNavigation({ request, url, origin }) {
  return (
    request?.mode === "navigate" &&
    request.method === "GET" &&
    Boolean(url) &&
    url.origin === origin &&
    !isApiPath(url.pathname) &&
    !isFileLikePath(url.pathname)
  );
}

/**
 * Kill-mode cache ownership (§22.3): only the caches whose names are exactly
 * the ones the Lyft Zone worker's Workbox configuration creates. V1 creates a
 * single precache and no runtime cache; any future owned cache must be added to
 * the owned list explicitly. Every other cache of the origin is left alone.
 */
export function selectOwnedCacheNames(cacheNames, ownedCacheNames) {
  const owned = new Set(ownedCacheNames.filter(Boolean));
  return cacheNames.filter((name) => owned.has(name));
}
